import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { sichereTextausgabe } from './winAnsi';
import type { Dokument, Knoten } from '../../components/bauordnung/dokument/dokumentbeschreibung';

/**
 * Uebersetzt die Dokumentbeschreibung in ein PDF — und sonst nichts.
 *
 * Diese Datei trifft KEINE fachliche Entscheidung. Was gedruckt wird, hat
 * `baueDokument()` entschieden; hier wird nur gezeichnet. Der Grund ist ein
 * eingetretener Fehler: ein Altgenerator hat Fachlogik nachgebaut und dabei
 * den Titel eines gesperrten Dokuments gedruckt.
 *
 * ═══ DAS INHALTSVERZEICHNIS ENTSTEHT IN ZWEI DURCHGAENGEN ═════════════════
 * Erst wird der Inhalt gesetzt und dabei je Ueberschrift die Seite notiert.
 * Danach wird das Verzeichnis eingeschoben und jede Seitenzahl um die Zahl
 * der eingeschobenen Seiten erhoeht. Ein einstufiges Verfahren stimmt genau
 * so lange, wie das Verzeichnis auf eine Seite passt — und ist ab der
 * zweiten Seite in JEDER Zeile falsch.
 */

const RAND = { links: 20, rechts: 20, oben: 20, unten: 22 };
const BREITE = 210;
const HOEHE = 297;
const TEXTBREITE = BREITE - RAND.links - RAND.rechts;

const SCHRIFT = { titel: 20, untertitel: 12, h1: 14, h2: 11.5, h3: 10.5, text: 9.5, klein: 8, winzig: 7 };

export interface BauordnungPdfOptionen {
    /** Fusszeile links, z. B. der Firmenname des Erlassenden. */
    fusszeileLinks?: string;
    /** Nur fuer Tests: erlaubt ein vorbereitetes Dokument. */
    __docFactory?: () => jsPDF;
}

interface Eintrag { text: string; nummer?: string; seite: number }

const zeilen = (doc: jsPDF, text: string, breite: number): string[] =>
    doc.splitTextToSize(text || '', breite) as string[];

export const renderBauordnungPdf = (
    dok: Dokument,
    optionen: BauordnungPdfOptionen = {},
): jsPDF => {
    const doc = optionen.__docFactory
        ? optionen.__docFactory()
        : new jsPDF({ format: 'a4', unit: 'mm', compress: true });

    // Letzte Rueckfallebene gegen UTF-16-Salat bei Umlauten. Am Dokument,
    // nicht an der einzelnen Ausgabe — eine vergessene Stelle genuegt sonst.
    sichereTextausgabe(doc);

    // ═══ Deckblatt ════════════════════════════════════════════════════════
    let y = 45;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(SCHRIFT.titel);
    doc.text(dok.titel.toUpperCase(), RAND.links, y);
    y += 9;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(SCHRIFT.untertitel);
    for (const z of zeilen(doc, dok.untertitel, TEXTBREITE)) {
        doc.text(z, RAND.links, y);
        y += 6;
    }
    y += 6;

    if (dok.deckblatt.length) {
        autoTable(doc, {
            startY: y,
            margin: { left: RAND.links, right: RAND.rechts },
            theme: 'grid',
            styles: { fontSize: SCHRIFT.text, cellPadding: 2.2, font: 'helvetica' },
            columnStyles: { 0: { cellWidth: 45, fontStyle: 'bold' } },
            body: dok.deckblatt.map(d => [d.merkmal, d.angabe]),
        });
        y = ((doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? y) + 10;
    }

    // Der Entwurfsvermerk steht auf Seite eins und nirgends sonst. Eine
    // freigegebene Fassung traegt ihn NICHT — sonst liest sich das Dokument
    // wie ein Entwurf, obwohl es unterschrieben ist.
    if (dok.entwurf) {
        doc.setFillColor(254, 243, 199);
        doc.setDrawColor(217, 119, 6);
        doc.rect(RAND.links, y, TEXTBREITE, 16, 'FD');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(SCHRIFT.klein);
        doc.setTextColor(120, 53, 15);
        doc.text('ENTWURF — noch nicht freigegeben', RAND.links + 4, y + 6);
        doc.setFont('helvetica', 'normal');
        doc.text('Diese Fassung ist nicht verbindlich und nicht zur Weitergabe bestimmt.', RAND.links + 4, y + 11.5);
        doc.setTextColor(0, 0, 0);
    }

    // ═══ Durchgang 1: der Inhalt ══════════════════════════════════════════
    const DECKBLATT_SEITEN = 1;
    const eintraege: Eintrag[] = [];
    doc.addPage();
    y = RAND.oben;

    const platzPruefen = (bedarf: number) => {
        if (y + bedarf > HOEHE - RAND.unten) {
            doc.addPage();
            y = RAND.oben;
        }
    };

    const absatz = (text: string, groesse: number, stil: 'normal' | 'bold' | 'italic', abstand = 4) => {
        doc.setFont('helvetica', stil);
        doc.setFontSize(groesse);
        for (const z of zeilen(doc, text, TEXTBREITE)) {
            platzPruefen(6);
            doc.text(z, RAND.links, y);
            y += groesse * 0.42 + 1.4;
        }
        y += abstand;
    };

    for (const k of dok.knoten as Knoten[]) {
        switch (k.art) {
            case 'seitenumbruch':
                doc.addPage();
                y = RAND.oben;
                break;

            case 'ueberschrift': {
                const groesse = k.ebene === 1 ? SCHRIFT.h1 : k.ebene === 2 ? SCHRIFT.h2 : SCHRIFT.h3;
                platzPruefen(18);
                if (k.ebene === 1) y += 3;
                const beschriftung = k.nummer ? `${k.nummer}  ${k.text}` : k.text;
                doc.setFont('helvetica', 'bold');
                doc.setFontSize(groesse);
                for (const z of zeilen(doc, beschriftung, TEXTBREITE)) {
                    platzPruefen(8);
                    doc.text(z, RAND.links, y);
                    y += groesse * 0.45 + 1;
                }
                if (k.ebene <= 2) {
                    doc.setDrawColor(203, 213, 225);
                    doc.line(RAND.links, y + 0.5, BREITE - RAND.rechts, y + 0.5);
                    y += 3.5;
                    eintraege.push({ text: k.text, nummer: k.nummer, seite: doc.getNumberOfPages() });
                } else {
                    y += 2.5;
                }
                break;
            }

            case 'absatz':
                absatz(k.text, SCHRIFT.text, k.betont ? 'italic' : 'normal');
                break;

            case 'liste':
                doc.setFont('helvetica', 'normal');
                doc.setFontSize(SCHRIFT.text);
                for (const punkt of k.punkte) {
                    const zs = zeilen(doc, punkt, TEXTBREITE - 6);
                    platzPruefen(zs.length * 5 + 2);
                    doc.text('•', RAND.links + 1, y);
                    for (const z of zs) {
                        doc.text(z, RAND.links + 6, y);
                        y += 4.6;
                    }
                    y += 1.6;
                }
                y += 3;
                break;

            case 'tabelle':
                platzPruefen(24);
                autoTable(doc, {
                    startY: y,
                    margin: { left: RAND.links, right: RAND.rechts },
                    theme: 'grid',
                    // Der Kopf wiederholt sich auf jeder Folgeseite — ohne ihn
                    // ist eine ueber zwei Seiten laufende Tabelle unlesbar.
                    showHead: 'everyPage',
                    styles: { fontSize: SCHRIFT.klein, cellPadding: 1.8, font: 'helvetica', overflow: 'linebreak' },
                    headStyles: { fillColor: [241, 245, 249], textColor: [30, 41, 59], fontStyle: 'bold' },
                    head: [k.kopf],
                    body: k.zeilen,
                });
                y = ((doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? y) + 6;
                break;

            case 'hinweis': {
                doc.setFont('helvetica', 'normal');
                doc.setFontSize(SCHRIFT.klein);
                const zs = zeilen(doc, k.text, TEXTBREITE - 8);
                platzPruefen(zs.length * 4.4 + 8);
                const hoehe = zs.length * 4.4 + 5;
                doc.setFillColor(240, 249, 255);
                doc.setDrawColor(186, 230, 253);
                doc.rect(RAND.links, y - 3.5, TEXTBREITE, hoehe, 'FD');
                let hy = y;
                for (const z of zs) {
                    doc.text(z, RAND.links + 4, hy);
                    hy += 4.4;
                }
                y = hy + 5;
                break;
            }

            case 'leerbefund':
                doc.setTextColor(120, 113, 108);
                absatz(k.text, SCHRIFT.klein, 'italic', 4);
                doc.setTextColor(0, 0, 0);
                break;

            case 'luecke': {
                doc.setFont('helvetica', 'bold');
                doc.setFontSize(SCHRIFT.klein);
                const kopf = `Offener Punkt Nr. ${k.nr}`;
                const zs = zeilen(doc, k.text, TEXTBREITE - 10);
                platzPruefen(zs.length * 4.4 + 12);
                doc.setFillColor(254, 249, 195);
                doc.setDrawColor(202, 138, 4);
                doc.rect(RAND.links, y - 3.5, TEXTBREITE, zs.length * 4.4 + 9, 'FD');
                doc.setTextColor(113, 63, 18);
                doc.text(kopf, RAND.links + 4, y);
                doc.setFont('helvetica', 'normal');
                let ly = y + 4.6;
                for (const z of zs) {
                    doc.text(z, RAND.links + 4, ly);
                    ly += 4.4;
                }
                doc.setTextColor(0, 0, 0);
                y = ly + 5;
                break;
            }

            case 'unterschriftsfeld':
                for (const zeile of k.zeilen) {
                    platzPruefen(30);
                    y += 10;
                    doc.setDrawColor(100, 116, 139);
                    doc.line(RAND.links, y, RAND.links + 75, y);
                    doc.line(RAND.links + 90, y, RAND.links + 165, y);
                    doc.setFont('helvetica', 'normal');
                    doc.setFontSize(SCHRIFT.winzig);
                    doc.text('Ort, Datum', RAND.links, y + 4);
                    doc.text(
                        [zeile.rolle, zeile.name, zeile.firma].filter(Boolean).join(' · ') || 'Unterschrift',
                        RAND.links + 90, y + 4,
                    );
                    y += 12;
                }
                y += 4;
                break;

            case 'entwurfsvermerk':
                absatz(k.text, SCHRIFT.klein, 'italic');
                break;

            default:
                break;
        }
    }

    // ═══ Durchgang 2: das Inhaltsverzeichnis einschieben ══════════════════
    if (eintraege.length) {
        const ZEILEN_JE_SEITE = 44;
        const tocSeiten = Math.max(1, Math.ceil(eintraege.length / ZEILEN_JE_SEITE));

        for (let i = 0; i < tocSeiten; i += 1) {
            doc.insertPage(DECKBLATT_SEITEN + 1 + i);
        }

        let index = 0;
        for (let s = 0; s < tocSeiten; s += 1) {
            doc.setPage(DECKBLATT_SEITEN + 1 + s);
            let ty = RAND.oben;
            if (s === 0) {
                doc.setFont('helvetica', 'bold');
                doc.setFontSize(SCHRIFT.h1);
                doc.text('Inhaltsverzeichnis', RAND.links, ty);
                ty += 10;
            }
            doc.setFontSize(SCHRIFT.text);
            for (let n = 0; n < ZEILEN_JE_SEITE && index < eintraege.length; n += 1, index += 1) {
                const e = eintraege[index];
                // JETZT stimmt die Zahl: der Inhalt ist um die eingeschobenen
                // Verzeichnisseiten nach hinten gerutscht.
                const seite = e.seite + tocSeiten;
                const beschriftung = e.nummer ? `${e.nummer}  ${e.text}` : e.text;
                doc.setFont('helvetica', e.nummer ? 'normal' : 'bold');
                const gekuerzt = zeilen(doc, beschriftung, TEXTBREITE - 14)[0] || beschriftung;
                doc.text(gekuerzt, RAND.links, ty);
                doc.text(String(seite), BREITE - RAND.rechts, ty, { align: 'right' });
                ty += 5.2;
            }
        }
    }

    // ═══ Fusszeilen — erst jetzt, weil vorher die Seitenzahl nicht feststeht
    const gesamt = doc.getNumberOfPages();
    for (let s = 1; s <= gesamt; s += 1) {
        doc.setPage(s);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(SCHRIFT.winzig);
        doc.setTextColor(100, 116, 139);
        doc.setDrawColor(226, 232, 240);
        doc.line(RAND.links, HOEHE - RAND.unten + 6, BREITE - RAND.rechts, HOEHE - RAND.unten + 6);

        const links = [optionen.fusszeileLinks, dok.titel].filter(Boolean).join(' · ');
        doc.text(links, RAND.links, HOEHE - RAND.unten + 10.5);
        doc.text(`Seite ${s} von ${gesamt}`, BREITE - RAND.rechts, HOEHE - RAND.unten + 10.5, { align: 'right' });

        // Der Verfasser steht KLEIN in der Fusszeile und nirgends im Kopf:
        // erlassen wird das Dokument vom Auftraggeber, verfasst nur.
        if (dok.verfasserZeile) {
            doc.text(dok.verfasserZeile, RAND.links, HOEHE - RAND.unten + 14.5);
        }
        doc.setTextColor(0, 0, 0);
    }

    return doc;
};
